import { useEffect, useState } from 'react';
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { AxiosError } from 'axios';

import appStorage from '@/modules/storage/appStorage';
import { setAxiosHeader } from '@/api/axiosInterceptors';
import { QUERY_KEYS } from '@/v2/constants/QueryKeys';

import retryPolicy from './retry/retryPolicy';
import onError from './onError/onError';
import onSuccess from './onSuccess/onSuccess';

export default function useQueryClientFactory() {
  const router = useRouter();

  const [queryClient] = useState(() => {
    const newQueryClient = new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          onError({ error, query, router, queryClient });
        },
      }),
      mutationCache: new MutationCache({
        onError: (error, variables, context, mutation) =>
          onError({ error, query: mutation, router, queryClient }),
        onSuccess,
      }),
      defaultOptions: {
        queries: {
          retry: retryPolicy,
          throwOnError: (error) => {
            if (error instanceof AxiosError) {
              // this is managed by the onError handler
              return false;
            }
            return true;
          },
        },
      },
    });

    restoreGlobalContext(newQueryClient);
    return newQueryClient;
  });

  useEffect(() => {
    window.__TANSTACK_QUERY_CLIENT__ = queryClient;
  }, [queryClient]);

  return { queryClient };
}

function restoreGlobalContext(queryClient) {
  const contextName = 'globalContext';
  const savedState = appStorage('localStorage').getItem(contextName);
  if (savedState) {
    const savedStateObject = JSON.parse(savedState);
    setAxiosHeader(savedStateObject.accessToken);

    const user = savedStateObject.user;
    queryClient.setQueryData([QUERY_KEYS.ME], user);
  }
}
